'use client';

import { useState, useEffect } from 'react';
import { useProductDetails } from '@app/pdp/hooks';
import { Heading, LayoutContainer, Tabs } from '@petsmart-ui/sparky';
import { trackTabClick } from '@libs/analytics';
import ProductDescriptionTabHtml from './components/ProductDescriptionTabHtml';
import type { ProductDescriptionProps } from './types';

export default function ProductDescription({
    productDescriptionMap,
    defaultSizeChartImgUrl = '',
}: ProductDescriptionProps) {
    const {
        selectedProduct: { sizeChartImgUrl, id },
    } = useProductDetails();

    const [sizeChartUrl, setSizeChartUrl] = useState<string>(
        defaultSizeChartImgUrl
    );

    useEffect(() => {
        if (sizeChartImgUrl) {
            setSizeChartUrl(sizeChartImgUrl);
        }
    }, [sizeChartImgUrl, setSizeChartUrl]);

    const productDescription = productDescriptionMap?.get(id);

    // Display nothing when there is no product description
    if (!productDescription) {
        return null;
    }

    const getTabSection = (key: string) =>
        productDescription?.[key as keyof typeof productDescription];

    const getTabKey = (index: number) =>
        productDescription && Object.keys(productDescription)[index];

    const onTabChange = (index: number) => {
        trackTabClick(getTabKey(index));
    };

    return (
        <LayoutContainer
            id="product-description"
            className="product-description"
            data-testid="product-description"
        >
            <Heading tagName="h5">About this item</Heading>
            <Tabs onChange={onTabChange}>
                {!!productDescription &&
                    Object.keys(productDescription).map(tabKey => (
                        <Tabs.Panel
                            key={tabKey}
                            title={tabKey}
                            className="product-description-tab"
                            data-testid="product-description-tab"
                        >
                            <div className="product-description-tab-wrapper">
                                <ProductDescriptionTabHtml
                                    htmlString={getTabSection(tabKey)}
                                    sizeChartImgUrl={sizeChartUrl}
                                    title={tabKey}
                                />
                            </div>
                        </Tabs.Panel>
                    ))}
            </Tabs>
        </LayoutContainer>
    );
}
